import * as _ from 'lodash'
import en_ca from 'date-fns/locale/en-CA';
import en_us from 'date-fns/locale/en-US';
import Emitter from './emitter';
import store from './store';
import enums from './enum';
import companyConfig from './companyConfig';
import globalConfig from './globalConfig';
import { Locale } from 'date-fns';

const dateLocales = {
	'en-ca': en_ca,
	'en-us': en_us
}

type TriggerFunction = (...args) => void;
type GlobalFunction = (ctx) => any | Promise<any>;

const _emitter = new Emitter();
const _globalFunctions: Record<string, GlobalFunction> = {};
const _stateFilters = {};

export enum WellKnownGlobalFunction {
	'GENERATE_APPROVED_CONTRACT_REPORTS' = 'GENERATE_APPROVED_CONTRACT_REPORTS',
	'GET_LEAD_SOURCES' = 'GET_LEADE_SOURCES',
	'GET_JOB_OVERVIEW_DOCUMENTS' = 'GET_JOB_OVERVIEW_DOCUMENTS'
}

interface GlobalRelay {
	config: null | Record<string, any>;
	version: null |string;
	environment: null | string;
	dateLocale: Locale;
	defaultSchemaVersion: number;
	clientMode: 'Default' | 'FieldApp';
	APP_BOOTSTRAPPED: string;
	APP_AUTH_CHANGE: string;
	JOB_COST_CREATE: string;
	JOB_COST_UPDATE: string;
	JOB_COST_BUILD_SUMMARY_LIST: string;
	JOB_COST_LINE_UPDATE: string;
	JOB_COST_SETTING_UPDATE: string;
	JOB_COST_SPECIFICATION_UPDATE: string;
	REQUEST_LIST: string;
	triggerAppBootstrapped: TriggerFunction;
	triggerAppAuthChange: TriggerFunction;
	triggerJobCostCreate: TriggerFunction;
	triggerJobCostUpdate: TriggerFunction;
	// NOTE: This allows company scripts to calculate taxes, warranties and fees
	triggerJobCostBuildSummaryList: TriggerFunction;
	triggerJobCostLineUpdate: TriggerFunction
	triggerJobCostSettingUpdate: TriggerFunction
	triggerJobCostSpecificationUpdate: TriggerFunction
	// NOTE: this allows company scripts to provide pre-filtered lists or other data, context will have things like the job, jobCost or other information necessary to filter, this will return a promise
	executeGlobalFunction(key: string, context?: any, required?: boolean): Promise<any>;
	// NOTE: this allows company scripts to alter component state, context will have things like the job, jobCost or other information necessary to evaluate any necessary state mutations, these are expected to run synchronously
	filterState(key, context, state): typeof state;
	registerGlobalFunction(key, func: GlobalFunction): void;
	registerStateFilter(key, filter): void;
	trigger(...args): void;
	on(message, handler): void;
	off(message): void;
}

const innerRelay: GlobalRelay = {
	config: null,
	version: null,
	environment: null,
	dateLocale: null,
	defaultSchemaVersion: 0,
	clientMode: 'Default',

	APP_BOOTSTRAPPED: 'relay.app.bootstrapped',
	APP_AUTH_CHANGE: 'relay.app.auth.change',
	JOB_COST_CREATE: 'relay.jobcost.create',
	JOB_COST_UPDATE: 'relay.jobcost.update',
	JOB_COST_BUILD_SUMMARY_LIST: 'relay.jobcostsummary.build-summary-list',
	JOB_COST_LINE_UPDATE: 'relay.jobcostlineitem.update',
	JOB_COST_SETTING_UPDATE: 'relay.jobcostsetting.update',
	JOB_COST_SPECIFICATION_UPDATE: 'relay.jobcostspecification.update',
	REQUEST_LIST: 'relay.list.request',

	triggerAppBootstrapped: function (...args) {
		_emitter.trigger(innerRelay.APP_BOOTSTRAPPED, ...args);
	},

	triggerAppAuthChange: function (...args) {
		_emitter.trigger(innerRelay.APP_AUTH_CHANGE, ...args);
	},

	triggerJobCostCreate: function (jobCost, ...args) {
		_emitter.trigger(innerRelay.JOB_COST_CREATE, jobCost, ...args);
	},

	triggerJobCostUpdate: function (jobCost, summary, path, ...args) {
		_emitter.trigger(innerRelay.JOB_COST_UPDATE, jobCost, summary, path, ...args);
	},

	// NOTE: This allows company scripts to calculate taxes, warranties and fees
	triggerJobCostBuildSummaryList: function (jobCostSummary, ...args) {
		_emitter.trigger(innerRelay.JOB_COST_BUILD_SUMMARY_LIST, jobCostSummary, ...args);
	},

	triggerJobCostLineUpdate: function (jobCostLineItem, path, jobCost, deleted, ...args) {
		_emitter.trigger(innerRelay.JOB_COST_LINE_UPDATE, jobCostLineItem, path, jobCost, deleted, ...args);
	},

	triggerJobCostSettingUpdate: function (jobCostSetting, jobCost, deleted, ...args) {
		_emitter.trigger(innerRelay.JOB_COST_SETTING_UPDATE, jobCostSetting, jobCost, deleted, ...args);
	},

	triggerJobCostSpecificationUpdate: function (jobCostSpecification, jobCost, deleted, ...args) {
		_emitter.trigger(innerRelay.JOB_COST_SPECIFICATION_UPDATE, jobCostSpecification, jobCost, deleted, ...args);
	},
	// NOTE: this allows company scripts to provide pre-filtered lists or other data, context will have things like the job, jobCost or other information necessary to filter, this will return a promise
	executeGlobalFunction: function (key, context, required) {
		const func = _globalFunctions[key];

		if (func) {
			return Promise.resolve(func(context));
		}
		else if(required) {
			const message = 'No global function found for key "${key}"';
			console.warn(message); // eslint-disable-line no-console
			return Promise.reject(message);
		}
		else {
			return Promise.resolve();
		}
	},

	// NOTE: this allows company scripts to alter component state, context will have things like the job, jobCost or other information necessary to evaluate any necessary state mutations, these are expected to run synchronously
	filterState: function (key, context, state) {
		const filter = _stateFilters[key];

		if (filter) {
			return filter(context, state);
		}

		return state;
	},

	registerGlobalFunction: function (key, func: GlobalFunction) {
		_globalFunctions[key] = func;
	},

	registerStateFilter: function (key, filter) {
		_stateFilters[key] = filter;
	},

	trigger: function (...args) {
		const [message, ...rest] = args;

		_emitter.trigger(message, rest);
	},

	on: function (message, handler) {
		_emitter.on(message, handler);
	},

	off: function (message) {
		_emitter.off(message);
	}
};

if (typeof globalThis !== 'undefined' && globalThis.Relay) {
	const globalRelay = globalThis.Relay;

	globalThis.Relay = {
		version: globalRelay.version,
		environment: globalRelay.environment
	};

	// initialize enums from Relay object
	_.forIn(globalRelay.enums, function (v, k) {
		enums.setTypeData(k, v);
	});

	// create a globalConfig proxy from config data
	(innerRelay.config as any) = globalConfig(_.merge({}, companyConfig, globalRelay.config)) as Record<string, any>;

	store().setIn(['model', 'domainEntities'], { jobStatuses: globalRelay.jobStatuses, leadSources: globalRelay.leadSources });

	innerRelay.version = globalRelay.version;
	innerRelay.environment = globalRelay.environment;
	innerRelay.defaultSchemaVersion = globalRelay.defaultSchemaVersion;
	innerRelay.clientMode = globalRelay.clientMode || 'default';

	const RELATIVE_LOCAL_FORMAT = {
		lastWeek: "'Last' eeee",
		yesterday: "'Yesterday'",
		today: "'Today'",
		tomorrow: "'Tomorrow'",
		nextWeek: "'Next' eeee",
		other: 'MM/dd/yyyy',
	};

	innerRelay.dateLocale = {
		...dateLocales[(innerRelay.config as any).localization.locale],
		formatRelative: (token) => RELATIVE_LOCAL_FORMAT[token],
	}
}

export default innerRelay;